import {
    BlockList,
    getSearchPosts,
    initializeApollo,
    initializeMFEs,
    getGraphQLData,
} from '@zillow/cms-blocks';
import { Heading, Paragraph } from '@zillow/constellation';
import { STICKY_SIDEBAR_TOP_PX } from '@zillow/zrs-cms';
import type { GetServerSideProps } from 'next';
import React from 'react';

import { PostsTemplate } from '../../components/PostsTemplate';
import type { BlocksString } from '../../types/blocks';
import type { Post } from '../../types/posts';
import { shouldUseVisualRefreshTheme } from '../document';

type BuildSearchPageInputs = {
    site: 'LEARN' | 'ZPA' | 'RENTALS' | 'CONSTRUCTION' | 'AGENTS';
    LayoutComponent: React.ComponentType<
        {
            children: React.ReactNode;
            useVisualRefreshTheme?: boolean;
        } & Pick<SearchPageContentProps, 'head' | 'menu' | 'preview' | 'header'>
    >;
} & Pick<SearchPageContentProps, 'basePath' | 'hasStickyNav'>;

export const SearchHero = ({
    searchTerm,
    total,
}: {
    searchTerm: string;
    total: number;
}): React.ReactNode => (
    <React.Fragment>
        <Paragraph fontColor="textMedium" fontType="h5">
            {total || 0} results for
        </Paragraph>
        <Heading level={1} fontType="h2">
            &ldquo;{searchTerm}&rdquo;
        </Heading>
    </React.Fragment>
);

type SearchPageContentProps = {
    // Examples: "/learn" or "/premieragent" or "premier-agent" or "".
    basePath: string;
    hasStickyNav?: boolean;
    footer?: { blocks: BlocksString };
    head: string;
    initialPage: number;
    preview?: boolean;
    menu?: unknown;
    posts: { total: number; pages: number; posts: Post[] };
    searchTerm: string;
    sidebar?: { blocks: BlocksString };
    header?: { disabled: boolean; transparent: boolean };
    useVisualRefreshTheme?: boolean;
};

export function buildSearchPage(inputs: BuildSearchPageInputs): {
    getServerSideProps: GetServerSideProps<SearchPageContentProps>;
    Page: React.ComponentType<SearchPageContentProps>;
} {
    const getServerSideProps: GetServerSideProps<
        SearchPageContentProps
    > = async (context) => {
        let page: number;
        let query: string;
        if (!context.params?.segments?.length) {
            // ?s=[query]&page=[page]
            if (!context.query.s || Array.isArray(context.query.s)) {
                return { notFound: true };
            }

            query = context.query.s;

            if (context.query.page) {
                page = Number(context.query.page);
            } else {
                page = 1;
            }
        } else if (context.params?.segments?.length === 1) {
            // /[query]?page=[page]
            // /[query]
            query = context.params.segments[0];

            if (context.query.page) {
                page = Number(context.query.page);
            } else {
                page = 1;
            }
        } else if (
            context.params?.segments?.length === 3 &&
            context.params.segments[1] === 'page' &&
            !Number.isNaN(context.params.segments[2])
        ) {
            // /[query]/page/[page]
            query = context.params.segments[0];
            page = Number(context.params.segments[2]);
        } else {
            return { notFound: true };
        }

        if (Number.isNaN(page)) {
            return { notFound: true };
        }

        initializeApollo();

        // Refresh MFE's to pick up changes on cache expiry
        initializeMFEs();

        let posts, settings, head;
        try {
            const result = await getSearchPosts({
                fetchPolicy: 'no-cache',
                variables: {
                    page: String(page),
                    perPage: '12',
                    search: query,
                    site: inputs.site,
                },
            });
            posts = result.posts;
            settings = result.settings;
            head = result.head;
        } catch (error) {
            if (
                error?.message ===
                getGraphQLData.ErrorCodes.ZG_GRAPH_PAGINATION_ERROR
            ) {
                return { notFound: true };
            }

            throw error;
        }

        // Throw a 404 if there are no posts
        if (!posts) {
            return {
                notFound: true,
            };
        }

        // Compute visual refresh path
        // Normalize basePath by trimming trailing slashes
        const normalizedBasePath = inputs.basePath.replace(/\/+$/, '');
        const visualRefreshPath = normalizedBasePath
            ? `${normalizedBasePath}/search/${query}/`
            : `/search/${query}/`;
        const useVisualRefreshTheme =
            shouldUseVisualRefreshTheme(visualRefreshPath);

        return {
            props: {
                basePath: inputs.basePath,
                head,
                initialPage: page,
                posts: posts || {},
                searchTerm: query,
                hasStickyNav: !!inputs.hasStickyNav,
                ...(settings?.footer && { footer: settings.footer }),
                ...(settings?.menu && { menu: settings.menu }),
                ...(settings?.sidebar && { sidebar: settings.sidebar }),
                useVisualRefreshTheme,
            },
        };
    };

    const SearchPageComponent = ({
        posts: { total },
        posts,
        initialPage = 1,
        searchTerm,
        footer,
        menu,
        sidebar,
        head,
        basePath,
        preview,
        hasStickyNav,
        useVisualRefreshTheme,
    }: SearchPageContentProps): React.ReactNode => (
        <inputs.LayoutComponent
            head={head}
            menu={menu}
            preview={preview}
            useVisualRefreshTheme={useVisualRefreshTheme}
        >
            <PostsTemplate
                posts={posts}
                incrementPaginationLinks
                initialPage={initialPage}
                query={{ s: searchTerm, orderBy: 'RELEVANCE' }}
                sidebar={
                    sidebar ? (
                        <BlockList
                            blocks={JSON.parse(sidebar?.blocks || '[]')}
                        />
                    ) : null
                }
                footer={footer}
                stickySidebarTop={
                    hasStickyNav ? `${STICKY_SIDEBAR_TOP_PX}px` : undefined
                }
                archiveCategory={undefined}
                hero={<SearchHero total={total} searchTerm={searchTerm} />}
                baseLink={`${basePath}/search/`}
            />
        </inputs.LayoutComponent>
    );

    return { getServerSideProps, Page: SearchPageComponent };
}
